import { history } from '@umijs/max';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { analytics, AnalyticsIdentity, IDENTITY_CHANGED, newId } from './client';
import type { SemanticContext } from './client';
import { canonicalPage } from './contract';
import { setPageView } from './context';
import type { AnalyticsExecutionSnapshot, AnalyticsSurface, EntityReference } from './contract-v2';

export const PRIVATE_SELECTOR = '[data-analytics-private], .StripeElement, input[type="password"]';
/** A semantic owner suppresses generic capture for this control and its children. */
export const SEMANTIC_SELECTOR = '[data-analytics-semantic]';

// Time is accumulated once, only while this tab is focused/visible and the user
// has interacted in the last minute. No keystroke contents or pointer paths.
export class EngagementClock {
  private accountedAt: number;
  private lastInteraction: number;
  private active: boolean;
  private pending = 0;
  constructor(now: number, active: boolean) {
    this.accountedAt = now;
    this.lastInteraction = now;
    this.active = active;
  }
  update(now: number, active = this.active, interacted = false) {
    if (this.active)
      this.pending += Math.max(0, Math.min(now, this.lastInteraction + 60000) - this.accountedAt);
    this.accountedAt = now;
    this.active = active;
    if (interacted) this.lastInteraction = now;
  }
  take(now: number) {
    this.update(now);
    const elapsed = this.pending;
    this.pending = 0;
    return elapsed;
  }
}

export function AnalyticsObserver({ children }: { children: React.ReactNode }) {
  const viewRef = useRef<{ id: string; path: string; identity: AnalyticsIdentity; clock: EngagementClock; emitted: boolean }>();
  // Publish the canonical page before descendant passive effects start requests.
  // The ref survives StrictMode effect replay, keeping one page fact and identity.
  useLayoutEffect(() => {
    if (!analytics.enabled) return;
    let view = viewRef.current;
    const focused = () => document.visibilityState === 'visible' && document.hasFocus();
    const emitEngagement = () => {
      if (!view) return;
      const active = view.clock.take(Date.now());
      if (active > 0)
        analytics.trackSemantic(
          'page_engagement',
          {},
          {
            page: canonicalPage(view.path),
            page_view_id: view.id,
            active_ms: active,
          },
          { ...view.identity, executionKind: 'automatic' },
        );
    };
    const navigate = () => {
      const path = history.location.pathname;
      analytics.setPage(path);
      analytics.touch();
      const identity = analytics.snapshot();
      if (!identity || (!identity.userId && !analytics.canTrack())) return;
      const sameView = (
        view?.path === path &&
        view.identity.userId === identity.userId &&
        view.identity.deviceId === identity.deviceId &&
        view.identity.sessionId === identity.sessionId
      );
      if (!sameView) {
        emitEngagement();
        view = viewRef.current = {
          id: newId(), path, identity,
          clock: new EngagementClock(Date.now(), focused()), emitted: false,
        };
      }
      if (!view) return;
      setPageView({ id: view.id, path: canonicalPage(path), sessionId: view.identity.sessionId });
      if (view.emitted) return;
      const result = analytics.trackSemantic(
        'page_viewed',
        {},
        {
          page_view_id: view.id,
          page: canonicalPage(path),
        },
        { ...view.identity, factKey: view.id, executionKind: 'automatic' },
      );
      view.emitted = result === 'queued' || result === 'duplicate';
    };
    const activity = () => {
      view?.clock.update(Date.now(), focused(), true);
      analytics.touch();
      if (view && view.identity.sessionId !== analytics.snapshot()?.sessionId) navigate();
    };
    const visibility = () => {
      view?.clock.update(Date.now(), focused());
      emitEngagement();
      if (!focused()) analytics.flush();
    };
    const pageHide = () => {
      view?.clock.update(Date.now(), false);
      emitEngagement();
      analytics.flush();
    };
    const click = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : undefined;
      const control = target?.closest(
        'button, a, [role="button"], [role="tab"], [role="option"], [data-analytics-action]',
      );
      if (
        !control ||
        control.closest(PRIVATE_SELECTOR) ||
        control.closest(SEMANTIC_SELECTOR) ||
        control.closest('[aria-disabled="true"], :disabled') ||
        history.location.pathname.startsWith('/user/')
      )
        return;
      const role = control.getAttribute('role');
      const resource = control.closest('[data-analytics-resource-id]');
      const rawResourceId = resource?.getAttribute('data-analytics-resource-id');
      const resourceId =
        rawResourceId && /^\d+$/.test(rawResourceId) && Number.isSafeInteger(Number(rawResourceId))
          ? Number(rawResourceId)
          : rawResourceId || undefined;
      const label = (
        control.getAttribute('data-analytics-label') ||
        control.getAttribute('aria-label') ||
        control.textContent ||
        ''
      )
        .trim()
        .slice(0, 120);
      analytics.track(role === 'option' ? 'suggestion_selected' : 'ui_interaction', {
        action: control.getAttribute('data-analytics-action') || label,
        control_id: control.id || undefined,
        element: control.tagName.toLowerCase(),
        role,
        resource_type: resource?.getAttribute('data-analytics-resource-type') || undefined,
        resource_id: resourceId,
        resource_name: resource?.getAttribute('data-analytics-resource-name') || undefined,
        resource_kind: resource?.getAttribute('data-analytics-resource-kind') || undefined,
        selected_before: resource?.hasAttribute('data-analytics-selected')
          ? resource.getAttribute('data-analytics-selected') === 'true'
          : undefined,
      });
    };
    const change = (event: Event) => {
      const target = event.target;
      if (
        !(
          target instanceof HTMLInputElement ||
          target instanceof HTMLSelectElement ||
          target instanceof HTMLTextAreaElement
        )
      )
        return;
      if (!target.closest('[data-analytics-filters]') || target.closest(PRIVATE_SELECTOR) || target.closest(SEMANTIC_SELECTOR)) return;
      analytics.track('filter_changed', {
        field:
          target.name ||
          target.id ||
          target.getAttribute('aria-label') ||
          target.getAttribute('placeholder') ||
          'filter',
        value:
          target instanceof HTMLInputElement && ['checkbox', 'radio'].includes(target.type)
            ? target.checked
            : target.value,
      });
    };
    const error = (event: ErrorEvent) => {
      // Error messages/stacks can contain customer input or secrets; Sentry owns
      // diagnostics. Amplitude records the category of the experience only.
      if (event.error)
        analytics.track('application_error', { error_type: event.error.name || 'Error' });
    };
    navigate();
    const unlisten = history.listen(navigate);
    window.addEventListener(IDENTITY_CHANGED, navigate);
    for (const event of ['pointerdown', 'keydown', 'scroll'])
      document.addEventListener(event, activity, { passive: true, capture: true });
    document.addEventListener('click', click, true);
    document.addEventListener('change', change, true);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('focus', visibility);
    window.addEventListener('blur', visibility);
    window.addEventListener('pagehide', pageHide);
    window.addEventListener('error', error);
    const interval = setInterval(emitEngagement, 30000);
    return () => {
      emitEngagement();
      unlisten();
      clearInterval(interval);
      window.removeEventListener(IDENTITY_CHANGED, navigate);
      for (const event of ['pointerdown', 'keydown', 'scroll'])
        document.removeEventListener(event, activity, true);
      document.removeEventListener('click', click, true);
      document.removeEventListener('change', change, true);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('focus', visibility);
      window.removeEventListener('blur', visibility);
      window.removeEventListener('pagehide', pageHide);
      window.removeEventListener('error', error);
    };
  }, []);
  return <>{children}</>;
}

export function useAnalyticsDetail(
  open: boolean,
  resourceType: string,
  resourceId: unknown,
  details: { name?: string; kind?: string; semantic?: SemanticDetailOptions } = {},
) {
  const previous = useRef('');
  const { name, kind } = details;
  useEffect(() => {
    const emit = () => {
      if (details.semantic) return;
      if (!open) {
        previous.current = '';
        return;
      }
      if (resourceId === null || resourceId === undefined || !analytics.canTrack()) return;
      const identity = analytics.snapshot();
      const key = `${identity?.userId}:${identity?.deviceId}:${resourceType}:${kind || ''}:${String(
        resourceId,
      )}`;
      if (previous.current === key) return;
      previous.current = key;
      analytics.track('detail_opened', {
        resource_type: resourceType,
        resource_id: resourceId,
        resource_name: name,
        resource_kind: kind,
        page: canonicalPage(history.location.pathname),
      });
    };
    emit();
    window.addEventListener(IDENTITY_CHANGED, emit);
    return () => window.removeEventListener(IDENTITY_CHANGED, emit);
  }, [open, resourceType, resourceId, name, kind, details.semantic]);
  useSemanticAnalyticsDetail(open, details.semantic);
}

export interface SemanticDetailOptions {
  readonly viewId: string;
  readonly context: SemanticContext & { surface: AnalyticsSurface };
  readonly resource: EntityReference;
  readonly resultPosition?: number;
  /** Initiating request/page snapshot; automatic completion must not borrow a later actor/session. */
  readonly execution?: AnalyticsExecutionSnapshot;
  /** Readiness is explicit; the feature owner emits its actual displayed fact. */
  readonly status?: 'loading' | 'ready' | 'failed';
  readonly failureReason?: string;
  readonly loadSource?: 'network' | 'cache';
}

export function useSemanticAnalyticsDetail(open: boolean, options?: SemanticDetailOptions) {
  useEffect(() => {
    if (!open || !options || options.execution?.excluded) return;
    const emit = () => {
      const execution = options.execution;
      const fields = {
        surface: options.context.surface,
        detail_view_id: options.viewId,
        resource: options.resource,
        result_position: options.resultPosition,
        load_source: options.loadSource,
      };
      const trackOptions = {
        factKey: options.viewId,
        executionKind: 'automatic' as const,
        // Pass every identity field even when a supplied snapshot is incomplete:
        // trackSemantic rejects it instead of filling gaps from the current actor.
        ...(execution
          ? {
              userId: execution.userId,
              deviceId: execution.deviceId,
              clientId: execution.clientId,
              accountEpoch: execution.accountEpoch,
              sessionId: execution.sessionId,
            }
          : {}),
      };
      const context = execution
        ? { ...options.context, execution_kind: 'automatic' as const }
        : options.context;
      const completionContext = execution
        ? {
            ...context,
            ...(execution.searchId ? { search_id: execution.searchId } : {}),
            ...(execution.operationId ? { operation_id: execution.operationId } : {}),
          }
        : context;
      analytics.trackSemantic('detail_opened', context, fields, trackOptions);
      if (options.status === 'ready')
        analytics.trackSemantic('detail_content_ready', completionContext, fields, trackOptions);
      if (options.status === 'failed')
        analytics.trackSemantic(
          'detail_content_failed',
          completionContext,
          { ...fields, reason: options.failureReason || 'unavailable' },
          trackOptions,
        );
    };
    emit();
    window.addEventListener(IDENTITY_CHANGED, emit);
    return () => window.removeEventListener(IDENTITY_CHANGED, emit);
  }, [open, options]);
}
